// Unified Library page with tabs for Videos, Frames, and GIFs
import { useState, useEffect, useCallback } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Film, Images, Gift, HardDrive } from 'lucide-react'
import { LibraryPage } from './library-page'
import { FramesLibraryPage } from './frames-library-page'
import { GifLibraryContent } from '@/features/gif/gif-library-content'
import { useStorageQuota } from '@/hooks/useStorageQuota'
import { useGifSessions } from '@/hooks/useGifSessions'
import { formatFileSize } from '@/lib/utils'

type LibraryTab = 'videos' | 'frames' | 'gifs'

interface UnifiedLibraryPageProps {
  embedded?: boolean
}

export function UnifiedLibraryPage({ embedded = false }: UnifiedLibraryPageProps) {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { quota: storageInfo } = useStorageQuota()
  const { sessions } = useGifSessions()
  
  // Determine initial tab from URL
  const getInitialTab = useCallback((): LibraryTab => {
    const path = location.pathname
    if (path.includes('/library/frames')) return 'frames'
    if (path.includes('/library/gifs')) return 'gifs'
    if (path.includes('/library/videos')) return 'videos'
    const libraryParam = searchParams.get('library')
    if (libraryParam === 'frames') return 'frames'
    if (libraryParam === 'gifs') return 'gifs'
    if (libraryParam === 'videos') return 'videos'
    // Check for tab query param
    const tabParam = searchParams.get('tab')
    if (tabParam === 'frames') return 'frames'
    if (tabParam === 'gifs') return 'gifs'
    return 'videos'
  }, [location.pathname, searchParams])
  
  const [activeTab, setActiveTab] = useState<LibraryTab>(getInitialTab)
  
  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    const tab = value as LibraryTab
    setActiveTab(tab)
    // Update URL without full navigation
    if (embedded) {
      const params = new URLSearchParams(searchParams)
      params.set('view', 'library')
      params.set('library', tab)
      setSearchParams(params, { replace: true })
      return
    }

    const newPath = tab === 'videos' ? '/library' : `/library/${tab}`
    window.history.replaceState(null, '', newPath)
  }
  
  // Sync with URL changes
  useEffect(() => {
    setActiveTab(getInitialTab())
  }, [getInitialTab])

  // Count items for badges (these would come from actual data)
  const videosCount = 0 // Will be populated by LibraryPage
  const framesCount = 0 // Will be populated by FramesLibraryPage
  const gifsCount = sessions?.length || 0

  return (
    <div className={embedded ? 'mx-auto max-w-7xl' : 'container mx-auto px-4 py-6 max-w-7xl'}>
      {/* Header with storage info */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">My Library</h1>
          <p className="text-muted-foreground text-sm">
            All your videos, frames, and GIFs in one place
          </p>
        </div>
        
        {/* Storage quota indicator */}
        {storageInfo && (
          <div className="flex items-center gap-3 bg-muted/50 rounded-lg px-4 py-2">
            <HardDrive className="h-4 w-4 text-muted-foreground" />
            <div className="flex-1 min-w-[120px]">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>Storage</span>
                <span>{formatFileSize(storageInfo.used)} / {formatFileSize(storageInfo.quota)}</span>
              </div>
              <Progress 
                value={storageInfo.percentUsed} 
                className="h-1.5"
              />
            </div>
          </div>
        )}
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-flex">
          <TabsTrigger value="videos" className="gap-2">
            <Film className="h-4 w-4" />
            <span>Videos</span>
            {videosCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                {videosCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="frames" className="gap-2">
            <Images className="h-4 w-4" />
            <span>Frames</span>
            {framesCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                {framesCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="gifs" className="gap-2">
            <Gift className="h-4 w-4" />
            <span>GIFs</span>
            {gifsCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                {gifsCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="videos" className="mt-0">
          <LibraryPage embedded />
        </TabsContent>
        
        <TabsContent value="frames" className="mt-0">
          <FramesLibraryPage embedded />
        </TabsContent>
        
        <TabsContent value="gifs" className="mt-0">
          <GifLibraryContent />
        </TabsContent>
      </Tabs>
    </div>
  )
}
